// Shared per-discipline authorization budget helpers (EMR-706).
//
// DISPLAY HELPERS ONLY — do not add authorization *rules* to this file.
//
// The rules that decide whether scheduling is blocked — which authorization
// covers a date, which visit types spend an authorized visit, what counts as
// "used", whether a batch fits — live in
// `backend/app/services/authorization_errors.py` and
// `backend/app/services/authorization_allocation.py`, and are read through
// `POST /episodes/{id}/authorizations/check`. The used/allowed numbers this
// file renders come from the server's `budget` rows; nothing here recomputes
// them.
//
// An earlier revision mirrored those rules here (a non-consuming visit-type
// set, an inactive-status set, a per-authorization used counter). The copy had
// already drifted from the backend — it was missing the transfer and death
// assessment types — which is exactly the failure mode: the browser shows one
// used/allowed number, the server blocks the save on a different one, and the
// user has no way to reconcile them. Two copies of one rule in two languages
// drift; there is no test that can be relied on to catch it, so the copy is
// simply not kept.
import { DISCIPLINE_ORDER, disciplineName } from '../constants/disciplines';
import type { DisciplineType } from '../services/api/models/DisciplineType';
import type { AuthorizationResponse } from '../services/api/types.gen';

export interface DisciplineVisitField {
  key: keyof AuthorizationResponse;
  label: string;
  discipline: string;
  full: string;
}

// The API column for each discipline's budget count. Excludes
// dietician_visits, misc_visits, rn_hours, lvn_hours (out of scope).
const VISIT_FIELD_KEY: Record<DisciplineType, keyof AuthorizationResponse> = {
  SN: 'sn_visits',
  PT: 'pt_visits',
  OT: 'ot_visits',
  ST: 'st_visits',
  HHA: 'hha_visits',
  MSW: 'msw_visits',
};

// Order and names come from the shared discipline map so this strip cannot
// drift from the rest of the UI.
export const DISCIPLINE_VISIT_FIELDS: DisciplineVisitField[] =
  DISCIPLINE_ORDER.map(discipline => ({
    key: VISIT_FIELD_KEY[discipline],
    discipline,
    label: discipline,
    full: disciplineName(discipline),
  }));

// Wire-contract error-code values from `backend/app/error_codes.py`
// (`AuthorizationErrorCode`). `AuthorizationError.code` is generated as a plain
// `string`, so nothing type-checks these -- keeping the one copy here means a
// backend rename breaks in one place instead of silently in several.
export const AUTHORIZATION_ERROR_CODE = {
  NO_AUTHORIZATION: 'no_authorization',
  UNLINKED_VISITS: 'unlinked_visits',
} as const;

/** Short human label for an authorization ("#12345" or "authorization"). */
export function authorizationLabel(
  auth: Pick<AuthorizationResponse, 'authorization_number'>
): string {
  return auth.authorization_number
    ? `#${auth.authorization_number}`
    : 'authorization';
}

/** One covering authorization is "at capacity" for a discipline when its used
 * count has reached its allowed budget -- the same `used >= allowed` boundary
 * the backend blocks on. */
export const disciplineAtCapacity = (used: number, allowed: number): boolean =>
  used >= allowed;
